import { create } from 'zustand';
import { BlogPost, Project, FaqItem, TeamMember, LegalPage, ContactSettings, Brand, SiteSettings, SeoSettings, ServiceCategory, HomePricingCard } from '../types';
import * as initialData from '../constants'; 

const DEFAULT_LEGAL_PAGES: LegalPage[] = [
  { id: 1, language: 'TR', title: 'Gizlilik Politikası', footerName: 'Gizlilik Politikası', slug: 'gizlilik-politikasi', status: 'YAYINDA', showInContactForm: true, showInCareerForm: false, content: '<h3>Gizlilik Politikası</h3><p>Buraya gizlilik politikası eklenecek.</p>' },
  { id: 2, language: 'TR', title: 'Kullanım Şartları', footerName: 'Kullanım Şartları', slug: 'kullanim-sartlari', status: 'YAYINDA', showInContactForm: false, showInCareerForm: false, content: '<h3>Kullanım Şartları</h3><p>Buraya kullanım şartları eklenecek.</p>' }
];

const DEFAULT_CONTACT_SETTINGS: ContactSettings = {
  phone: "+90 (555) 123 45 67",
  email: "info@patoyazilim.com",
  address: "İstanbul, Türkiye",
  whatsapp: "+905551234567",
  instagram: "https://instagram.com/patoyazilim",
  linkedin: "https://linkedin.com/company/patoyazilim",
  twitter: "https://twitter.com/patoyazilim"
};

const DEFAULT_BRANDS: Brand[] = [
  { id: 1, name: "Marka 1", logo: "https://placehold.co/200x100?text=Marka+1" }
];

const DEFAULT_SITE_SETTINGS: SiteSettings = {
  maintenanceMode: false,
  gaId: '',
  clarityId: ''
};

const DEFAULT_SEO_SETTINGS: SeoSettings[] = [
  { page: 'home', language: 'TR', title: 'Ana Sayfa - Pato Yazılım', description: 'Pato Yazılım Ana Sayfa', keywords: 'yazılım, web' },
  { page: 'home', language: 'EN', title: 'Home - Pato Yazılım', description: 'Pato Yazılım Home', keywords: 'software, web' },
];

interface DataState {
  serviceCategories: ServiceCategory[];
  homePricingCards: HomePricingCard[];
  blogPosts: BlogPost[];
  projects: Project[];
  faqs: FaqItem[];
  teamMembers: TeamMember[];
  brands: Brand[];
  legalPages: LegalPage[];
  contactSettings: ContactSettings;
  siteSettings: SiteSettings;
  seoSettings: SeoSettings[];
  
  updateServiceCategories: (categories: ServiceCategory[]) => void;
  updateHomePricingCards: (cards: HomePricingCard[]) => void;
  updateProjects: (projects: Project[]) => void;
  updateBlogPosts: (posts: BlogPost[]) => void;
  updateTeamMembers: (members: TeamMember[]) => void;
  updateFaqs: (faqs: FaqItem[]) => void;
  updateLegalPages: (pages: LegalPage[]) => void;
  updateContactSettings: (settings: ContactSettings) => void;
  updateBrands: (brands: Brand[]) => void;
  updateSiteSettings: (settings: SiteSettings) => void;
  updateSeoSettings: (settings: SeoSettings[]) => void;
  addProject: (project: Project) => void;
  deleteProject: (id: number) => void;
  addTeamMember: (member: TeamMember) => void;
  deleteTeamMember: (id: number) => void;
}

const getInitialState = () => {
  const saved = localStorage.getItem('pato_site_data');
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      // Merge projects: keep parsed projects, but add any new ones from initialData that aren't in parsed
      const mergedProjects = [...(parsed.projects || [])];
      initialData.PROJECTS.forEach(initProj => {
        if (!mergedProjects.find(p => p.id === initProj.id)) {
          mergedProjects.push(initProj);
        }
      });

      return {
        ...parsed,
        serviceCategories: parsed.serviceCategories || initialData.SERVICE_CATEGORIES,
        homePricingCards: parsed.homePricingCards || initialData.HOME_PRICING_CARDS,
        legalPages: parsed.legalPages || DEFAULT_LEGAL_PAGES,
        contactSettings: parsed.contactSettings || DEFAULT_CONTACT_SETTINGS,
        brands: parsed.brands || DEFAULT_BRANDS,
        faqs: parsed.faqs || initialData.FAQS,
        siteSettings: parsed.siteSettings || DEFAULT_SITE_SETTINGS,
        seoSettings: parsed.seoSettings || DEFAULT_SEO_SETTINGS,
        projects: mergedProjects
      };
    } catch (e) {
      console.error("Failed to parse saved data", e);
    }
  }
  return {
    serviceCategories: initialData.SERVICE_CATEGORIES,
    homePricingCards: initialData.HOME_PRICING_CARDS,
    blogPosts: initialData.BLOG_POSTS,
    projects: initialData.PROJECTS,
    faqs: initialData.FAQS,
    teamMembers: initialData.TEAM_MEMBERS,
    legalPages: DEFAULT_LEGAL_PAGES,
    contactSettings: DEFAULT_CONTACT_SETTINGS,
    brands: DEFAULT_BRANDS,
    siteSettings: DEFAULT_SITE_SETTINGS,
    seoSettings: DEFAULT_SEO_SETTINGS,
  };
};

export const useDataStore = create<DataState>((set, get) => {
  return {
    ...getInitialState(),

    updateServiceCategories: (serviceCategories) => {
      set({ serviceCategories });
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    },

    updateHomePricingCards: (homePricingCards) => {
      set({ homePricingCards });
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    },

    updateProjects: (projects) => {
      set({ projects });
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    },

    updateBlogPosts: (blogPosts) => {
      set({ blogPosts });
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    },

    updateTeamMembers: (teamMembers) => {
      set({ teamMembers });
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    },

    updateFaqs: (faqs) => {
      set({ faqs });
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    },

    updateLegalPages: (legalPages) => {
      set({ legalPages });
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    },

    updateContactSettings: (contactSettings) => {
      set({ contactSettings });
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    },

    updateBrands: (brands) => {
      set({ brands });
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    },

    updateSiteSettings: (siteSettings) => {
      set({ siteSettings });
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    },

    updateSeoSettings: (seoSettings) => {
      set({ seoSettings });
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    },

    addProject: (project) => {
      set((state) => ({ projects: [project, ...state.projects] }));
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    },

    deleteProject: (id) => {
      set((state) => ({ projects: state.projects.filter(p => p.id !== id) }));
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    },

    addTeamMember: (member) => {
      set((state) => ({ teamMembers: [...state.teamMembers, member] }));
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    },

    deleteTeamMember: (id) => {
      set((state) => ({ teamMembers: state.teamMembers.filter(m => m.id !== id) }));
      localStorage.setItem('pato_site_data', JSON.stringify(get()));
    }
  };
});
